
import type { PropType } from 'vue'

import type { 
    DispatcherWorkplaceDataEntity,
    DispatcherWorkplaceTake,
    DispatcherWorkplaceDelivery,
    DispatcherWorkplaceItem
} from '@/modules/DispatcherWorkplaceModule/types'

import { defineComponent, computed } from 'vue'

import DwSidebarEntity from './Entity.vue'
import DwSidebarFilter from './Filter.vue'

import DwButton from '@/modules/DispatcherWorkplaceModule/ui/DwButton/index.vue'

export default defineComponent({
    components: {
        'dw-sidebar-entity': DwSidebarEntity,
        'dw-sidebar-filter': DwSidebarFilter,
        'dw-button': DwButton,
    },
    props: {
        entities: {
            type: Array as PropType<Array<DispatcherWorkplaceDataEntity>>,
            required: true
        },
        isCompletedOrdersEnabled: {
            type: Boolean,
            default: false,
        },
        isDeliveriesEnabled: {
            type: Boolean,
            default: false,
        },
        isUndeliveredDeliveriesEnabled: {
            type: Boolean,
            default: false,
        },
        isTakesEnabled: {
            type: Boolean,
            default: false,
        },
        isUnassignedTakesEnabled: {
            type: Boolean,
            default: false,
        },
        selectedUnassignedTakes: {
            type: Array as PropType<Array<number>>,
            default: () => []
        },
        selectedSidebarFeatureId: {
            type: String as PropType<string | null>,
            default: null
        }
    },
    emits: ['entity-clicked', 'toggle-unassigned-take', 'mass-appointment'],
    setup(props, { emit }) {
        const onEntityClicked = (entity: DispatcherWorkplaceDataEntity) => {
            emit('entity-clicked', entity)
        }

        const isTakeCompleted = (entity: DispatcherWorkplaceItem) => {
            if (entity.type === 1) {
                return entity.state === 1 || entity.statusId === 3
            }

            return false
        }

        const isDeliveryCompleted = (entity: DispatcherWorkplaceItem) => {
            if (entity.type === 2) {
                return entity.state === 1
            }

            return false
        }

        const isFeatureEntityCompleted = (entity: DispatcherWorkplaceItem) => {
            if (entity.type === 1) {
                return isTakeCompleted(entity)
            }

            if (entity.type === 2) {
                return isDeliveryCompleted(entity)
            }

            return false
        }

        const toggleUnassignedTake = (take_id: number) => {
            emit('toggle-unassigned-take', take_id)
        }

        const onMassAppointment = () => {
            emit('mass-appointment')
        }

        const sortOrders = (orders: DispatcherWorkplaceDataEntity[]): DispatcherWorkplaceDataEntity[] => {
            return orders.sort((a, b) => {
                const getPriority = (order: DispatcherWorkplaceDataEntity): number => {
                    if (order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned) return 0 // Неназначенные заборы
                    if (order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered) return 1 // Невыданные доставки
                    if (!isFeatureEntityCompleted(order)) return 2 // Незавершённые
                    return 3 // Завершённые
                }

                return getPriority(a) - getPriority(b)
            })
        }

        const computedOrders = computed(() => {
            let ordersToRender: DispatcherWorkplaceDataEntity[] = []

            const completedTakes = props.entities.filter(isTakeCompleted)
            const completedDeliveries = props.entities.filter(isDeliveryCompleted)

            const takes = props.entities.filter((order) => order.type === 1 && !((order as DispatcherWorkplaceTake).notAssigned) && !isFeatureEntityCompleted(order))
            const deliveries = props.entities.filter((order) => order.type === 2 && !((order as DispatcherWorkplaceDelivery).notDelivered) && !isFeatureEntityCompleted(order))

            const unassigned = props.entities.filter((order) => order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned)
            const undelivered = props.entities.filter((order) => order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered)

            ordersToRender = props.entities.filter((order) => {
                const isCompletedTake = isTakeCompleted(order)
                const isCompletedDelivery = isDeliveryCompleted(order)

                const isTake = order.type === 1 && !(order as DispatcherWorkplaceTake).notAssigned && !isFeatureEntityCompleted(order)
                const isDelivery = order.type === 2 && !(order as DispatcherWorkplaceDelivery).notDelivered && !isFeatureEntityCompleted(order)
                const isUnassignedTake = order.type === 1 && (order as DispatcherWorkplaceTake).notAssigned
                const isUndeliveredDelivery = order.type === 2 && (order as DispatcherWorkplaceDelivery).notDelivered
                return !isCompletedTake && !isCompletedDelivery && !isTake && !isDelivery && !isUnassignedTake && !isUndeliveredDelivery
            })

            if (props.isCompletedOrdersEnabled) {
                if (props.isTakesEnabled) {
                    ordersToRender = ordersToRender.concat(completedTakes)
                }

                if (props.isDeliveriesEnabled) {
                    ordersToRender = ordersToRender.concat(completedDeliveries)
                }
            } else {
                if (props.isTakesEnabled) {
                    ordersToRender = ordersToRender.concat(takes)
                }

                if (props.isDeliveriesEnabled) {
                    ordersToRender = ordersToRender.concat(deliveries)
                }
            }

            if (props.isUnassignedTakesEnabled) {
                ordersToRender = ordersToRender.concat(unassigned)
            }

            if (props.isUndeliveredDeliveriesEnabled) {
                ordersToRender = ordersToRender.concat(undelivered)
            }

            return sortOrders(ordersToRender)
        })

        return {
            onEntityClicked,
            onMassAppointment,
            toggleUnassignedTake,
            computedOrders,
        }
    }
})
